// import * as ui from 'datagrok-api/ui';
import * as DG from 'datagrok-api/dg';
import * as grok from 'datagrok-api/grok';
import { tanimotoSimilarity } from '@datagrok-libraries/ml/src/distance-metrics-methods';
import { HELM_CORE_FIELDS, jsonSdfMonomerLibDict, MONOMER_ENCODE_MAX, MONOMER_ENCODE_MIN, SDF_MONOMER_NAME, } from '../utils/const';
import { splitAlignedSequences } from '../utils/splitter';
import { GAP_SYMBOL } from '../utils/macromolecule/consts';
import { getMonomerLibHelper } from '../types/monomer-library';
export function encodeMonomers(col, seqHelper) {
    let encodeSymbol = MONOMER_ENCODE_MIN;
    const monomerSymbolDict = {};
    const sh = seqHelper.getSeqHandler(col);
    const encodedStringArray = [];
    const rowCount = col.length;
    for (let rowIdx = 0; rowIdx < rowCount; ++rowIdx) {
        let encodedMonomerStr = '';
        const monomers = sh.getSplitted(rowIdx);
        for (let posIdx = 0; posIdx < rowCount; ++posIdx) {
            const cm = monomers.getCanonical(posIdx);
            if (!monomerSymbolDict[cm]) {
                if (encodeSymbol > MONOMER_ENCODE_MAX) {
                    grok.shell.error(`Not enough symbols to encode monomers`);
                    return null;
                }
                monomerSymbolDict[cm] = encodeSymbol;
                encodeSymbol++;
            }
            encodedMonomerStr += String.fromCodePoint(monomerSymbolDict[cm]);
        }
        encodedStringArray.push(encodedMonomerStr);
    }
    return DG.Column.fromStrings('encodedMolecules', encodedStringArray);
}
export function getMolfilesFromSeq(col, monomersLibObject, seqHelper) {
    const sh = seqHelper.getSeqHandler(col);
    const monomersDict = createMomomersMolDict(monomersLibObject);
    const molFiles = [];
    const rowCount = col.length;
    for (let rowIdx = 0; rowIdx < rowCount; ++rowIdx) {
        const monomers = sh.getSplitted(rowIdx);
        const molFilesForSeq = [];
        for (let j = 0; j < rowCount; ++j) {
            const cm = monomers.getCanonical(j);
            if (cm) {
                if (!monomersDict[cm]) {
                    grok.shell.warning(`Monomer ${cm} is missing in HELM library. Structure cannot be created`);
                    return null;
                }
                // what is the reason of double conversion?
                molFilesForSeq.push(JSON.parse(JSON.stringify(monomersDict[cm])));
            }
        }
        molFiles.push(molFilesForSeq);
    }
    return molFiles;
}
export function getMolfilesFromSingleSeq(cell, monomersLibObject, seqHelper) {
    const sh = seqHelper.getSeqHandler(cell.column);
    const monomersDict = createMomomersMolDict(monomersLibObject);
    const molFiles = [];
    const monomers = sh.getSplitted(cell.rowIndex);
    const molFilesForSeq = [];
    for (let j = 0; j < monomers.length; ++j) {
        const cm = monomers.getCanonical(j);
        if (cm) {
            if (!monomersDict[cm]) {
                grok.shell.warning(`Monomer ${cm} is missing in HELM library. Structure cannot be created`);
                return null;
            }
            molFilesForSeq.push(JSON.parse(JSON.stringify(monomersDict[cm])));
        }
    }
    molFiles.push(molFilesForSeq);
    return molFiles;
}
export function createMomomersMolDict(lib) {
    const dict = {};
    lib.forEach((it) => {
        if (it['polymerType'] === 'PEPTIDE') {
            const monomerObject = {};
            HELM_CORE_FIELDS.forEach((field) => {
                monomerObject[field] = it[field];
            });
            dict[it["symbol" /* HELM_FIELDS.SYMBOL */]] = monomerObject;
        }
    });
    return dict;
}
export function createJsonMonomerLibFromSdf(table) {
    var _a, _b, _c, _d, _e;
    const resultLib = [];
    for (let i = 0; i < table.rowCount; i++) {
        const monomer = {};
        Object.keys(jsonSdfMonomerLibDict).forEach((key) => {
            if (key === "symbol" /* HELM_FIELDS.SYMBOL */) {
                const monomerSymbol = table.get(jsonSdfMonomerLibDict[key], i);
                monomer[key] = monomerSymbol === '.' ? table.get(SDF_MONOMER_NAME, i) : monomerSymbol;
            }
            else if (key === "rgroups" /* HELM_FIELDS.RGROUPS */) {
                const rgroups = table.get(jsonSdfMonomerLibDict[key], i).split('\n');
                const jsonRgroups = [];
                rgroups.forEach((g) => {
                    const rgroup = {};
                    const altAtom = g.substring(g.lastIndexOf(']') + 1);
                    const radicalNum = g.match(/\[R(\d+)\]/)[1];
                    rgroup["capGroupSmiles" /* HELM_RGROUP_FIELDS.CAP_GROUP_SMILES */] = altAtom === 'H' ? `[*:${radicalNum}][H]` : `O[*:${radicalNum}]`;
                    rgroup["alternateId" /* HELM_RGROUP_FIELDS.ALTERNATE_ID */] = altAtom === 'H' ? `R${radicalNum}-H` : `R${radicalNum}-OH`;
                    rgroup["capGroupName" /* HELM_RGROUP_FIELDS.CAP_GROUP_NAME */] = altAtom === 'H' ? `H` : `OH`;
                    rgroup["label" /* HELM_RGROUP_FIELDS.LABEL */] = `R${radicalNum}`;
                    jsonRgroups.push(rgroup);
                });
                monomer[key] = jsonRgroups;
            }
            else {
                if (jsonSdfMonomerLibDict[key])
                    monomer[key] = table.get(jsonSdfMonomerLibDict[key], i);
            }
        });
        monomer[_a = "author" /* HELM_REQUIRED_FIELD.AUTHOR */] ?? (monomer[_a] = '');
        monomer[_b = "id" /* HELM_REQUIRED_FIELD.ID */] ?? (monomer[_b] = 0);
        monomer[_c = "smiles" /* HELM_REQUIRED_FIELD.SMILES */] ?? (monomer[_c] = '');
        monomer[_d = "monomerType" /* HELM_REQUIRED_FIELD.MONOMER_TYPE */] ?? (monomer[_d] = 'Backbone');
        monomer[_e = "createDate" /* HELM_REQUIRED_FIELD.CREATE_DATE */] ?? (monomer[_e] = null);
        resultLib.push(monomer);
    }
    return resultLib;
}
export async function sequenceChemSimilarity(positionColumns, referenceSequence, seqHelper) {
    /* eslint-enable max-len */
    if (positionColumns instanceof DG.Column)
        positionColumns = splitAlignedSequences(positionColumns, seqHelper).columns.toList();
    const libHelper = await getMonomerLibHelper();
    const monomerLib = libHelper.getMonomerLib();
    // const smilesCols: DG.Column<string>[] = new Array(monomerCols.length);
    const rawCols = new Array(positionColumns.length);
    const rowCount = positionColumns[0].length;
    const totalSimilarity = new Float32Array(rowCount);
    // Calculate base similarity
    for (let position = 0; position < positionColumns.length; ++position) {
        const referenceMonomerCanonical = position < referenceSequence.length ?
            referenceSequence.getCanonical(position) : GAP_SYMBOL;
        const referenceMol = monomerLib.getMonomer('PEPTIDE', referenceMonomerCanonical)?.smiles ?? '';
        const monomerCol = positionColumns[position];
        const monomerColData = monomerCol.getRawData();
        const monomerColCategories = monomerCol.categories;
        const emptyCategoryIdx = monomerColCategories.indexOf('');
        rawCols[position] = { categories: monomerColCategories, data: monomerColData, emptyIndex: emptyCategoryIdx };
        if (typeof referenceMonomerCanonical === 'undefined')
            continue;
        // Calculating similarity for
        const molCol = DG.Column.fromStrings('smiles', monomerColCategories.map((cat) => monomerLib.getMonomer('PEPTIDE', cat)?.smiles ?? ''));
        const _df = DG.DataFrame.fromColumns([molCol]); // getSimilarities expects that column is in dataframe
        const similarityCol = (await grok.chem.getSimilarities(molCol, referenceMol));
        const similarityColData = similarityCol ? similarityCol.getRawData() : null;
        for (let rowIdx = 0; rowIdx < rowCount; ++rowIdx) {
            const monomerCategoryIdx = monomerColData[rowIdx];
            if (referenceMonomerCanonical !== GAP_SYMBOL && monomerCategoryIdx !== emptyCategoryIdx)
                totalSimilarity[rowIdx] += similarityColData[monomerCategoryIdx];
            else if (referenceMonomerCanonical === GAP_SYMBOL && monomerCategoryIdx === emptyCategoryIdx)
                totalSimilarity[rowIdx] += 1;
            // Do not increase similarity on mismatch score/penalty equals 0;
        }
    }
    for (let similarityIndex = 0; similarityIndex < totalSimilarity.length; ++similarityIndex) {
        let updatedSimilarity = totalSimilarity[similarityIndex] / referenceSequence.length;
        for (let position = 0; position < positionColumns.length; ++position) {
            const currentRawCol = rawCols[position];
            if ((position >= referenceSequence.length && currentRawCol.data[similarityIndex] !== currentRawCol.emptyIndex) ||
                (currentRawCol.data[similarityIndex] === currentRawCol.emptyIndex && position < referenceSequence.length)) {
                updatedSimilarity = DG.FLOAT_NULL;
                break;
            }
        }
        totalSimilarity[similarityIndex] = updatedSimilarity;
    }
    const similarityCol = DG.Column.fromFloat32Array('Similarity', totalSimilarity);
    return similarityCol;
}
/* eslint-disable max-len */
/** Calculates chemical similarity between each pair of monomers.
 * @param {string[]} monomerSet Set of unique monomers.
 * @returns {Promise<{scoringMatrix: number[][], alphabetIndexes: {[monomerId: string]: number}}>} Ojbect containing similarity scoring matrix and monomer to index mapping. */
export async function calculateMonomerSimilarity(monomerSet) {
    /* eslint-enable max-len */
    const libHelper = await getMonomerLibHelper();
    const monomerLib = libHelper.getMonomerLib();
    const scoringMatrix = [];
    const alphabetIndexes = {};
    const monomerMolecules = monomerSet.map((monomer) => monomerLib.getMonomer('PEPTIDE', monomer)?.smiles ?? '');
    const monomerMoleculesCol = DG.Column.fromStrings('smiles', monomerMolecules);
    for (let monomerIndex = 0; monomerIndex < monomerMolecules.length; ++monomerIndex) {
        const monomer = monomerSet[monomerIndex];
        alphabetIndexes[monomer] = monomerIndex;
        const monomerMol = monomerMolecules[monomerIndex];
        const similarityScores = monomerMol === '' ? new Array(monomerMolecules.length).fill(0) :
            (await grok.chem.getSimilarities(monomerMoleculesCol, monomerMol)).getRawData();
        similarityScores[monomerIndex] = 1;
        scoringMatrix[monomerIndex] = Array.from(similarityScores);
    }
    return { scoringMatrix, alphabetIndexes };
}
export async function getMonomerSubstitutionMatrix(monomerSet, fingerprintType = 'Morgan') {
    const libHelper = await getMonomerLibHelper();
    const monomerLib = libHelper.getMonomerLib();
    const scoringMatrix = new Array(monomerSet.length).fill(0).map(() => new Array(monomerSet.length).fill(0));
    const alphabetIndexes = {};
    // note, below specifically boolean OR is used to get either molfile or smiles, because we want to skip '' molfiles
    const monomerMolecules = monomerSet
        .map((monomer) => monomerLib
        .getMonomer('PEPTIDE', monomer)?.molfile || monomerLib.getMonomer('PEPTIDE', monomer)?.smiles || '');
    const fingerprintsFunc = DG.Func.find({ package: 'Chem', name: 'getFingerprints' })[0];
    if (!fingerprintsFunc) {
        console.warn('Function "Chem:getFingerprints" is not found in chem package. falling back to Morgan fingerprints');
        return await calculateMonomerSimilarity(monomerSet);
    }
    const monomerMoleculesCol = DG.Column.fromStrings('smiles', monomerMolecules);
    // needed for function to work
    const _unusedMolDf = DG.DataFrame.fromColumns([monomerMoleculesCol]);
    const fingerPrints = (await fingerprintsFunc.apply({ col: monomerMoleculesCol, fingerprintType: fingerprintType }))?.entries;
    if (!fingerPrints) {
        console.warn(`${fingerprintType} Fingerprints could not be calculated for monomers from chem package.
      falling back to Morgan fingerprints`);
        return await calculateMonomerSimilarity(monomerSet);
    }
    for (let i = 0; i < fingerPrints.length; ++i) {
        scoringMatrix[i][i] = 1;
        alphabetIndexes[monomerSet[i]] = i;
        if (!fingerPrints[i])
            continue;
        for (let j = i + 1; j < fingerPrints.length; ++j) {
            if (!fingerPrints[j])
                continue;
            // @ts-ignore
            scoringMatrix[i][j] = scoringMatrix[j][i] = tanimotoSimilarity(fingerPrints[i], fingerPrints[j]);
        }
    }
    return { scoringMatrix, alphabetIndexes };
}
;
//# sourceMappingURL=monomer-utils.js.map